import { useSettings } from "@app-composables/settings";
import useDebugDialog from "@app-composables/use-debug-dialog";
import usei18n from "@app-i18n/index";
import useCompanyRestClient from "@app-rest/company";
import { CUSTOM_BACKEND_URL_STORAGE_KEY, resolveAny } from "@app-rest/index";
import useCompanyStore from "@app-store/company";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { QButton, QFormFieldInput, QModal } from "@qamf/lighthouse";
import { useObservability } from "@qamf/observability";
import { useCloudBackend } from "@qamf/shell-app-sdk";
import { computed, defineComponent, onBeforeMount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
	name: "QSettings",
	components: {
		QButton,
		QFormFieldInput,
		QModal
	},
	props: {
		modelValue: { required: true, type: Boolean }
	},
	emits: ["update:modelValue"],
	setup(props, { emit }) {
		const { setOption: setCloudBackendOption, getOption: getCloudBackendOption } = useCloudBackend();
		const { trackEvent } = useObservability();
		const { translateKey } = usei18n();
		const { cloudBackendUrl: defaultCloudBackendUrl } = useSettings();
		const route = useRoute();
		const companyStore = useCompanyStore();

		const cloudBackendUrl = ref(getCloudBackendOption("host"));
		const initialCloudBackendHost = ref(getCloudBackendOption("host"));
		const isTestingConnection = ref(false);
		const connectionTestFailed = ref(false);

		const modal = ref<{ openDialog: () => void }>();

		// QModal ignores an initial modelValue=true
		onMounted(() => {
			useDebugDialog().takeOverOpenDevSettings();
			if (props.modelValue) modal.value?.openDialog();
		});

		const modalOpenValue = computed({
			get: () => props.modelValue,
			set: (value) => emit("update:modelValue", value)
		});

		const testBackendConnection = async(host: string) => {
			const companyId = Number(route.params.companyId) || companyStore.Id;
			if (!companyId) return true;

			const previousHost = getCloudBackendOption("host");
			setCloudBackendOption("host", host);

			const [, error] = await resolveAny(useCompanyRestClient().info(companyId));
			if (error) setCloudBackendOption("host", previousHost);

			return !error;
		};

		const sendCustomSettings = async() => {
			if (!cloudBackendUrl.value) return;

			connectionTestFailed.value = false;
			isTestingConnection.value = true;
			const isReachable = await testBackendConnection(cloudBackendUrl.value);
			isTestingConnection.value = false;

			if (!isReachable) {
				connectionTestFailed.value = true;
				return;
			}

			sessionStorage.setItem(CUSTOM_BACKEND_URL_STORAGE_KEY, cloudBackendUrl.value);

			trackEvent(AppInsightsEvent.CustomizedBackendUrl, {
				mode: "edit",
				cloudBackendHost: cloudBackendUrl.value
			});

			window.location.reload();
		};

		const resetCustomSettings = () => {
			cloudBackendUrl.value = initialCloudBackendHost.value;
			connectionTestFailed.value = false;

			trackEvent(AppInsightsEvent.CustomizedBackendUrl, {
				mode: "reset",
				...(cloudBackendUrl.value && { cloudBackendHost: cloudBackendUrl.value })
			});

			modalOpenValue.value = false;
		};

		const resetToDefaultUrl = () => {
			cloudBackendUrl.value = defaultCloudBackendUrl.value;
		};

		const onCloseSettings = () => {
			if (cloudBackendUrl.value === "" || !cloudBackendUrl.value)
				cloudBackendUrl.value = getCloudBackendOption("host");

			connectionTestFailed.value = false;
		};

		onBeforeMount(() => {
			cloudBackendUrl.value = getCloudBackendOption("host");
			initialCloudBackendHost.value = getCloudBackendOption("host");
		});

		return {
			cloudBackendUrl,
			sendCustomSettings,
			resetCustomSettings,
			resetToDefaultUrl,
			modal,
			modalOpenValue,
			onCloseSettings,
			isTestingConnection,
			connectionTestFailed,
			translateKey
		};
	}
});
